import { useProjectParams } from '@hooks/useProjectParams';
import { BadgeWorkOrderStatus } from 'components/BadgeWorgOrderStatus';
import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import type { IDropdownContextProps } from 'components/DropdownContext';
import { DropdownContext } from 'components/DropdownContext';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalLoading } from 'components/ModalLoading';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ProjectData, ProjectStatusEnum } from 'gql/graphql';
import { ContractStatusEnum, ProjectContractTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useMethodsWorkOrder } from 'hooks/useMethodsWorkOrder';
import { useWorkOrderChangeCreate } from 'hooks/useWorkOrderChangeCreate';
import { useWorkOrderDownload } from 'hooks/useWorkOrderDownload';
import { useWorkOrderPricesExport } from 'hooks/useWorkOrderPricesExport';
import { BimWorkOrderChangeCreateOrEdit } from 'pages/Project/BimProject/WorkOrderChangeCreateOrEdit';
import { workOrderVersionsGql } from 'pages/Project/UerProject/WorkOrder/workOrderQueries';
import { WorkOrderRequisites } from 'pages/Project/UerProject/WorkOrder/WorkOrderRequisites';
import { WorkOrderChapters } from 'pages/Project/UerProject/WorkOrder/workorderTypes';
import { CreateClosingWorkOrderChange } from 'pages/Project/UerProject/WorkOrderChange/CreateClosingWorkOrderChange/CreateClosingWorkOrderChange';
import { WorkOrderChangeCreateOrEdit } from 'pages/Project/UerProject/WorkOrderChange/WorkOrderChangeCreateOrEdit';
import type { BadgePropSize } from 'uikit';
import { cnMixSpace, IconAlert, Tooltip } from 'uikit';

import * as R from 'ramda';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useUerExportWorkOrderPrices } from '../../hooks/useUerExportWorkOrderPrices';
import { methodologyMapReverse } from '../../types/methodology';

import { useAddItemsKebabTreeWorkOrder } from './useAddItemsKebabTreeWorkOrder';

interface IKebabTreeWorkOrder extends Partial<IDropdownContextProps> {
    itemId: number;
    contractId: number;
    orderRules: IUseAccess['order'];
    projectData: ProjectData | undefined | null;
    workOrderLabel: string;
    statusProject: ProjectStatusEnum | undefined;
    isWorkOrderVorTypeXml?: boolean;
    isActKC2Navs?: boolean;
    statusContract?: ContractStatusEnum;
    statusWorkOrder?: WorkOrderStatusEnum;
    kebabBadgeSize?: BadgePropSize;
    isWorkOrderChangeRequired: boolean;
    hasInProgress?: boolean;
    refreshProject: () => void;
}

export const KebabTreeWorkOrder = ({
    itemId,
    contractId,
    kebabBadgeSize,
    statusProject,
    statusWorkOrder,
    statusContract,
    refreshProject,
    orderRules,
    projectData,
    workOrderLabel,
    isWorkOrderVorTypeXml,
    isActKC2Navs,
    isWorkOrderChangeRequired,
    hasInProgress,
    ...propsDropdown
}: IKebabTreeWorkOrder) => {
    const dispatch = useDispatch();

    const { orderId, methodology } = useProjectParams();

    const workOrderId = Number(orderId);

    // @ts-ignore
    const { filters } = useSelector(({ filtersWorkOrder }) => filtersWorkOrder);

    const [isOpenWorkOrderRequisites, setIsOpenWorkOrderRequisites] = useState<boolean>(false);
    const [isOpenWorkOrderCreateChange, setIsOpenWorkOrderCreateChange] = useState<boolean>(false);
    const [isCreateClosingWorkOrderChangeOpen, setIsCreateClosingWorkOrderChangeOpen] = useState<boolean>(false);

    const switchIsCreateClosingWorkOrderChangeOpen = useCallback(
        () => setIsCreateClosingWorkOrderChangeOpen(R.not),
        [],
    );

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;
    const isSummaryChapterSelected = filters.chapter === WorkOrderChapters.SUMMARY;

    const {
        deleteData,
        deleteStatus,
        deleteError,
        isSubmitted,
        isDialogOpen,
        handleDeleteButton,
        handleDialogClose,
        handleDeleteWorkOrder,
        handleCloseModalError,
        handleCreateReport,
    } = useMethodsWorkOrder(itemId);

    const {
        isSubmittedDownload,
        workOrderAsExcelData,
        workOrderAsExcelStatus,
        workOrderAsExcelError,
        workOrderAsExcelAbort,
        handleDownloadWorkOrder,
        handleCloseModalErrorDownload,
        handleCreateReportDownload,
    } = useWorkOrderDownload({
        workOrderId: itemId,
        instanceId: 'fromTree',
        methodology: methodology ? methodologyMapReverse[methodology] : undefined,
        workOrderStatus: statusWorkOrder,
    });

    const { doUerExportWorkOrderPricesRequest, doUerExportWorkOrderPricesAbort, uerExportWorkOrderPricesStatus } =
        useUerExportWorkOrderPrices({ workOrderId: itemId });

    const { isLoadingBimExportWorkOrderPrices, handleBimExportWorkOrderPrices, exportWorkOrderPricesAbort } =
        useWorkOrderPricesExport({ workOrderId: itemId });

    const [, , , workOrderVersionsRequest] = useRequest({
        requestId: 'workOrderVersions',
        query: workOrderVersionsGql,
        withEffect: false,
    });

    const isLoadingUerExportWorkOrderPrices = useMemo(
        () => uerExportWorkOrderPricesStatus === RequestStatus.FETCHING,
        [uerExportWorkOrderPricesStatus],
    );

    const isLoadingWorkOrder = useMemo(
        () => workOrderAsExcelStatus === RequestStatus.FETCHING,
        [workOrderAsExcelStatus],
    );

    const abortLoading = useMemo(() => {
        if (isLoadingWorkOrder) {
            return workOrderAsExcelAbort;
        }

        return isLoadingUerExportWorkOrderPrices ? doUerExportWorkOrderPricesAbort : exportWorkOrderPricesAbort;
    }, [
        doUerExportWorkOrderPricesAbort,
        exportWorkOrderPricesAbort,
        isLoadingUerExportWorkOrderPrices,
        isLoadingWorkOrder,
        workOrderAsExcelAbort,
    ]);

    const isWorkOrderRequisitesDisabled = useMemo(
        () => isSummaryChapterSelected || !isContractStatusWork || !orderRules.canEdit,
        [isSummaryChapterSelected, isContractStatusWork, orderRules.canEdit],
    );

    const isCreateWorkOrderChangeDisabled = useMemo(() => false, []);

    const handleRequisitesChanged = useCallback(
        (callVersionsRenew: boolean) => {
            if (refreshProject) {
                refreshProject();
            }

            if (callVersionsRenew && Number.isFinite(workOrderId)) {
                dispatch(workOrderVersionsRequest({ workOrderId }));
            }
        },
        [dispatch, refreshProject, workOrderId, workOrderVersionsRequest],
    );

    const { closeCreatingWorkOrderChangeHandler, isCreateWorkOrderChangeOpened, openCreatingWorkOrderChangeHandler } =
        useWorkOrderChangeCreate({
            workOrderId: itemId,
        });

    const {
        groupsWorkOrderKebabMenu,
        actionsWorkOrder,
        getMenuItemAttributes,
        tooltipPosition,
        DeleteOrderErrorHandler,
        DeleteOrderConfirmDialog,
        onMenuMouseMove,
        onMenuMouseLeave,
        activeTooltipMsg,
    } = useAddItemsKebabTreeWorkOrder({
        projectData,
        statusProject,
        statusContract,
        statusWorkOrder,
        filters,
        orderRules,
        itemId,
        isActKC2Navs,
        isWorkOrderVorTypeXml,
        methodology,
        hasInProgress,
        setIsOpenWorkOrderRequisites,
        setIsOpenWorkOrderCreateChange,
        handleDownloadWorkOrder,
        handleDownloadWorkOrderPrices: doUerExportWorkOrderPricesRequest,
        handleDeleteButton,
        handleBimExportWorkOrderPrices,
        openCreatingWorkOrderChangeHandler,
        isWorkOrderChangeRequired,
        openCreateClosingWorkOrderChangeHandler: switchIsCreateClosingWorkOrderChangeOpen,
    });

    const getComponentBadge = () => {
        if (statusWorkOrder === WorkOrderStatusEnum.InProgress && isWorkOrderChangeRequired) {
            return (
                <ComponentWithTooltip
                    tooltipText="Содержит несогласованные изменения"
                    status="warning"
                    className={cnMixSpace({ mR: 'xs' })}
                    styles={{
                        border: '3px solid transparent',
                        boxSizing: 'border-box',
                        width: '16px',
                        height: '16px',
                    }}
                >
                    <IconAlert size="xs" view="warning" />
                </ComponentWithTooltip>
            );
        }

        if (statusWorkOrder) {
            return <BadgeWorkOrderStatus size="s" status={statusWorkOrder} />;
        }

        return null;
    };

    const isFrameContract = useMemo(
        () => projectData?.contractType === ProjectContractTypeEnum.Frame,
        [projectData?.contractType],
    );

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {getComponentBadge()}
            <DropdownContext
                items={actionsWorkOrder}
                groups={groupsWorkOrderKebabMenu}
                getItemAttributesHandler={getMenuItemAttributes}
                onMouseMoveHandler={onMenuMouseMove}
                onMouseLeaveHandler={onMenuMouseLeave}
                {...propsDropdown}
            />
            {activeTooltipMsg && (
                <Tooltip
                    size={activeTooltipMsg.length > 80 ? 'm' : 's'}
                    direction="rightCenter"
                    position={tooltipPosition}
                    isInteractive={false}
                    style={{ zIndex: 2 }}
                >
                    {activeTooltipMsg}
                </Tooltip>
            )}
            <ConfirmationDialog
                textHeader="УДАЛЕНИЕ НАРЯД-ЗАКАЗА"
                textConfirmation="Вы уверены, что хотите удалить текущий наряд-заказ?"
                cancelButtonText="Не удалять"
                confirmButtonText="Удалить"
                isOpened={isDialogOpen}
                onSubmit={handleDeleteWorkOrder}
                onClose={handleDialogClose}
            />
            {isOpenWorkOrderRequisites && (
                <WorkOrderRequisites
                    workOrderId={itemId}
                    onChange={handleRequisitesChanged}
                    isDisabled={isWorkOrderRequisitesDisabled}
                    isOpenOuter={isOpenWorkOrderRequisites}
                    setIsOpenOuter={setIsOpenWorkOrderRequisites}
                    withButton={false}
                    orderRules={orderRules}
                />
            )}
            {isOpenWorkOrderCreateChange && (
                <WorkOrderChangeCreateOrEdit
                    contractId={contractId}
                    workOrderId={itemId}
                    isDisabled={isCreateWorkOrderChangeDisabled}
                    isOpenedOuter={isOpenWorkOrderCreateChange}
                    setIsOpenOuter={setIsOpenWorkOrderCreateChange}
                    withButton={false}
                    projectData={projectData}
                    refreshProjectData={refreshProject}
                    workOrderLabel={workOrderLabel}
                    statusContract={statusContract}
                    statusProject={statusProject}
                    isCreating
                />
            )}
            {isCreateWorkOrderChangeOpened && (
                <BimWorkOrderChangeCreateOrEdit
                    contractStatus={statusContract}
                    isCreating
                    isFrameContract={isFrameContract}
                    onClose={closeCreatingWorkOrderChangeHandler}
                    workOrderId={itemId}
                    projectData={projectData}
                    refreshProjectData={refreshProject}
                />
            )}
            {isCreateClosingWorkOrderChangeOpen && (
                <CreateClosingWorkOrderChange
                    workOrderId={itemId}
                    onClose={switchIsCreateClosingWorkOrderChangeOpen}
                    refreshProjectData={refreshProject}
                />
            )}
            <ErrorHandlingModal
                titleText="Удаление наряд-заказа"
                isSubmitted={isSubmitted}
                statusRequest={deleteStatus}
                dataRequest={deleteData?.uerDeleteWorkOrder}
                errorsRequest={deleteError}
                handleCloseModalError={handleCloseModalError}
                handleCreateReport={handleCreateReport}
            />
            <ErrorHandlingModal
                titleText="Выгрузка наряд-заказа"
                isSubmitted={isSubmittedDownload}
                statusRequest={workOrderAsExcelStatus}
                dataRequest={workOrderAsExcelData}
                errorsRequest={workOrderAsExcelError}
                handleCloseModalError={handleCloseModalErrorDownload}
                handleCreateReport={handleCreateReportDownload}
            />
            {(isLoadingWorkOrder || isLoadingBimExportWorkOrderPrices || isLoadingUerExportWorkOrderPrices) && (
                <ModalLoading
                    title={
                        isLoadingWorkOrder
                            ? 'Выгрузка Наряд-заказа'
                            : 'Выгрузка накопительной ведомости по Наряд-Заказу'
                    }
                    onClose={abortLoading}
                />
            )}
            {DeleteOrderErrorHandler}
            {DeleteOrderConfirmDialog}
        </div>
    );
};

KebabTreeWorkOrder.displayName = 'KebabTreeWorkOrder';
